import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMoralisQuery } from 'react-moralis';
import { ethers } from 'ethers';
import { GameColorsProvider } from 'src/providers/Theme/gameColorsProvider';
import { containerMaxWXl, ContentContainer } from '../../components/ContentContainer/contentContainer';
import { CoverBackground } from '../../components/CoverBackground/coverBackground';
import { NoGame } from './Body/noGame';
import { Box, Flex, Grid, Spinner } from '@chakra-ui/react';
import { RightPanel } from './Body/rightPanel';
import { GameContent } from './Content/content';
import { GameOverviewHeader } from './Header/gameOverviewHeader';
import { SearchPane } from './Header/searchPane';
import { createStandaloneToast } from '@chakra-ui/react';
import { useBlockchainProvider } from '../../providers/Blockchain/blockchainProvider';
import { emptyGame, IGame } from 'src/providers/Games/useGames';
import { t } from 'i18next';
import Moralis from 'moralis-v1';
import { blockchainConfig } from '../../blockchain-config';
import { TransactionsList } from '../../components/TransactionsList/transactionsList';
import { GameUserProvider } from '../../providers/GameUser/gameUserProvider';
import { contrastColors, trimAddress } from '../../utils/parser';
import { CodeSnippet } from './Content/CodeSnippet/codeSnippet';
import useFetch from 'src/utils/hooks/useFetch';

export enum GameState {
	LOADING,
	FOUND,
	UNKNOWN,
	INVALID,
}

export const GameOverview = () => {
	const { id } = useParams();
	const { toast } = createStandaloneToast();
	const { account } = useBlockchainProvider();

	const [state, handleState] = useState<GameState>(GameState.LOADING);
	const [providedGameData, setProvidedGameData] = useState<IGame>(emptyGame);

	const [isCheckedForNewsletter, handleCheckForNewsletter] = useState(false);

	const [buddy, setBuddy] = useState('');
	const [buddyParam] = useSearchParams(window.location.search);

	const [userBalance, setUserBalance] = useState('0');

	const { data, isLoading } = useMoralisQuery('Games', query => query.equalTo('address', id?.toLowerCase()), []);

	useEffect(() => {
		console.log(providedGameData);
	}, [providedGameData]);

	const {
		data: gameDataJSON,
		loading,
		error,
	} = useFetch('https://open-api.hashup.it/v1/token/migration/metadata/' + id);

	const actionTextColor = useMemo(
		() => (contrastColors(providedGameData.colors.actionColor, '#ffffff') > 3.1 ? 'white' : 'black'),
		[state]
	);

	useEffect(() => {
		console.log(gameDataJSON);
	}, [gameDataJSON]);

	buddyParam.get('buddy');

	useEffect(() => setBuddy(buddyParam.toString().substring(6)), [buddyParam]);

	useEffect(() => {
		if (buddy && buddy !== account && account !== ethers.constants.AddressZero) {
			localStorage.setItem('buddy', buddy);
			toast({ title: `Using ${trimAddress(buddy)} as referral` });
		}
	}, [buddy, account]);

	useEffect(() => {
		(async () => {
			if (account === ethers.constants.AddressZero || !id || !ethers.utils.isAddress(id)) {
				return;
			}

			const tokenData = (
				await Moralis.Web3API.account.getTokenBalances({
					chain: blockchainConfig.NETWORK_DEFAULT.NAME as Moralis.Chain as any,
					address: account,
					token_addresses: [id],
				})
			)[0];

			setUserBalance(tokenData?.balance ?? '0');
		})();
	}, [account, id]);

	const handleCheckbox = (event: any) => {
		handleCheckForNewsletter(event.target.checked);
	};

	const typeOptions = {
		[GameState.LOADING]: () => (
			<Flex
				width="100%"
				minH="100vh"
				justifyContent="center"
				alignItems="center"
			>
				<Spinner size="xl" />
			</Flex>
		),
		[GameState.FOUND]: () => {
			return (
				<Box
					color={providedGameData.colors.textColor}
					minWidth={{ base: 'unset', hashup: '1168px', xl: '1240px' }}
				>
					<Box display={{ base: 'block', hashup: 'none' }}>
						<SearchPane
							customStyle={{
								bg: providedGameData.colors.backgroundColor,
								text: providedGameData.colors.textColor,
								cta: providedGameData.colors.actionColor,
							}}
						/>
						<GameOverviewHeader gameData={providedGameData} />
					</Box>
					<Box
						mb="156px"
						px={{ base: '16px', hashup: '32px', xl: '0' }}
						w="100%"
						pos="relative"
						maxW={containerMaxWXl}
					>
						<Box display={{ base: 'none', hashup: 'block' }}>
							<GameOverviewHeader gameData={providedGameData} />
						</Box>
						<Grid
							w="100%"
							gridGap={{ base: '24px', hashup: '32px' }}
							templateColumns={{ base: 'calc(100vw - 64px)', hashup: '840px 400px' }}
						>
							<Flex
								flexDirection="column"
								flex=".7"
								gridGap="0px"
								pos="relative"
							>
								<GameContent
									about={providedGameData.description}
									gamePaper={providedGameData.gamepaper}
									screens={providedGameData.screenshots.filter((screen: string) => screen !== '')}
									video={providedGameData.video}
									cta={providedGameData.colors.actionColor}
									providedGameData={providedGameData}
								/>
								<CodeSnippet
									header="JSON Game data"
									data={JSON.stringify(gameDataJSON ?? {}, null, 2) ?? 'Loading'}
								/>
								<CodeSnippet
									header="Sell this game in React app!"
									data={`import { useHashup } from "@hashup-it/hashup-react-sdk"  

const { buyGame } = useHashup()

function handleBuy() {
    const license = "${id}" // Specify address of license you want to buy
    const amount = 1 // Specify amount of license you want to buy  
										
    buyGame(license, amount)  
}  `}
								/>
							</Flex>
							<Flex
								flex=".3"
								flexDirection="column"
								gridGap="24px"
							>
								<RightPanel
									developer={providedGameData.creator}
									gamers="--"
									releaseDate={providedGameData.createdAt}
									totalSupply={(Number(providedGameData.totalSupply) / 100).toString()}
									transfers="--"
									title={providedGameData.name}
									symbol={providedGameData.symbol}
									type={providedGameData.color}
									thumbsUp="--"
									thumbsDown="--"
									url={providedGameData.gameUrl}
									id={providedGameData.address}
									idOwned={false}
									gamePaper={providedGameData.gamepaper}
									logo={providedGameData.media.icon ?? providedGameData.media.logoUrl}
									newsletter={{
										handleCheckForNewsletter,
										handleCheckbox,
										isCheckedForNewsletter,
									}}
									gameData={providedGameData}
								/>
							</Flex>
						</Grid>
						<Box marginTop="32px">
							<TransactionsList
								headerRightText={t('home.seeMore').toString()}
								header={t('home.latestTransaction').toString()}
								headerStyle={{
									fontSize: '14px',
									lineHeight: '18px',
									fontWeight: '400',
									textTransform: 'uppercase',
								}}
								onClickRightHeader={() => null}
								filter={{ game: { address: id ?? ethers.constants.AddressZero } }}
								customTheme={providedGameData.colors}
							/>
						</Box>
					</Box>
				</Box>
			);
		},
		[GameState.UNKNOWN]: () => <NoGame reason={t('gameOverview.noGame').toString()} />,
		[GameState.INVALID]: () => <NoGame reason="Invalid address" />,
	};

	const connectType = useMemo(() => {
		if (state === GameState.LOADING) {
			return GameState.LOADING;
		}
		if (state === GameState.FOUND) {
			return GameState.FOUND;
		}
		if (state === GameState.UNKNOWN) {
			return GameState.UNKNOWN;
		}
		if (state === GameState.INVALID) {
			return GameState.INVALID;
		}
		return GameState.LOADING;
	}, [state]);

	useEffect(() => {
		if (isLoading) {
			handleState(GameState.LOADING);
		} else if (data.length > 0) {
			const payload = (data as any)[0].attributes as IGame;
			console.log((data as any)[0].attributes);
			setProvidedGameData(payload);

			handleState(GameState.FOUND);
		} else if (data.length === 0 && id && ethers.utils.isAddress(id)) {
			handleState(GameState.UNKNOWN);
		} else if (data.length === 0 && id && !ethers.utils.isAddress(id)) {
			handleState(GameState.INVALID);
		}
	}, [id, data, isLoading]);

	const body = useMemo(() => typeOptions[connectType](), [isCheckedForNewsletter, state]);
	return (
		<GameColorsProvider
			value={{
				textColor: providedGameData.colors.textColor,
				backgroundColor: providedGameData.colors.backgroundColor,
				callToActionColor: providedGameData.colors.actionColor,
				callToActionTextColor: actionTextColor,
			}}
		>
			<GameUserProvider value={{ balance: userBalance }}>
				<ContentContainer isCustomThemed>
					<CoverBackground
						coverPhoto={providedGameData.media.coverImageUrl}
						isNonGradient={true}
					>
						{body}
					</CoverBackground>
				</ContentContainer>
			</GameUserProvider>
		</GameColorsProvider>
	);
};
